import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Alert } from '@mui/material';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { setSelectedEdge } from '../../../features/networkSlice';
import { setEdges } from '../../../features/networkUndoSlice';
import { sameId, idKey, signLabel } from '../../../lib/model';
import { handleAltArrowReorder } from './reorderShortcut';

function CustomFilter() {
  return <GridToolbarQuickFilter sx={{ p: 2 }} />;
}

const initialState = { pagination: { paginationModel: { pageSize: 50 } } };

export default function EdgesTable(props) {
  const { visible } = props; // indicates if the tab is visible (otherwise DataGrid will complain)
  const edges = useSelector((state) => state.networkUndo.present.edges);
  const nodes = useSelector((state) => state.networkUndo.present.nodes);
  const highlightedNodes = useSelector((state) => state.network.highlightedNodes) || [];
  const highlightedEdges = useSelector((state) => state.network.highlightedEdges) || [];

  const filteredEdges = edges.filter(
    (edge) => (highlightedNodes.length === 0 && highlightedEdges.length === 0)
      || highlightedNodes.some((id) => sameId(id, edge.to) || sameId(id, edge.from))
      || highlightedEdges.some((id) => sameId(id, edge.id)),
  );
  const nodesById = nodes.reduce((acc, item) => {
    acc[idKey(item.id)] = item;
    return acc;
  }, {});

  const endpointLabel = ({ value }) => {
    const label = nodesById[idKey(value)]?.label;
    return label && String(label) !== String(value) ? `${label} (${value})` : String(value);
  };
  const columns = [
    { field: 'id', headerName: 'ID', flex: 1 },
    {
      field: 'from',
      flex: 2,
      headerName: 'From',
      valueFormatter: endpointLabel,
    },
    {
      field: 'to',
      flex: 2,
      headerName: 'To',
      valueFormatter: endpointLabel,
    },
    {
      field: 'sign',
      flex: 1,
      headerName: 'Sign',
      valueFormatter: (params) => signLabel(params.value),
    },
    { field: 'description', flex: 3, headerName: 'Description', valueGetter: ({ row }) => row.description || row.title || '' },
  ];

  const dispatch = useDispatch();

  const onRowClick = (e) => { // 'onRowDoubleClick' also available
    dispatch(setSelectedEdge(e.row));
  };

  return (visible && (
    <>
      <DataGrid
        autoHeight
        getRowHeight={() => 'auto'}
        sx={{
          '& .MuiDataGrid-cell': {
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            alignItems: 'start',
            py: 1,
          },
          border: 0,
        }}
        rows={filteredEdges}
        columns={columns}
        initialState={initialState}
        onRowDoubleClick={onRowClick}
        onCellKeyDown={(params, event) => handleAltArrowReorder(
          edges,
          params.row.id,
          event,
          dispatch,
          setEdges,
        )}
        disableRowSelectionOnClick
        pageSizeOptions={[5, 10, 25, 50]}
        slots={{ toolbar: CustomFilter }}
      />
      {(highlightedEdges.length > 0 || highlightedNodes.length > 0) && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          The rows are filtered based on selections
        </Alert>
      )}
    </>
  ));
}
